import { useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Download, FileSpreadsheet, Loader2, Receipt, Store } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { fmtInt, fmtNumber, fmtRiyadhDateTime, isoFromDate, lastDayOfMonth } from "@/lib/format";
import { fmtPeriod, fmtDay, blobErrorMessage } from "@/components/stock-count/scUtils";
import { PosCard } from "./PosTable";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;
const EXPORT_TIMEOUT_MS = 120000;
const DAILY_SHOWN = 7;

function ym(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The last 13 months, newest first, as "YYYY-MM". */
function monthOptions() {
    const now = new Date();
    return Array.from({ length: 13 }, (_, i) => ym(new Date(now.getFullYear(), now.getMonth() - i, 1)));
}

/** The month holding yesterday: the last day with POS data, so this month to date once
 *  the month has a full day behind it (on the 1st it is still last month). */
function yesterdayMonth() {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return ym(y);
}

/** Save a blob response as a file (name from Content-Disposition). */
function saveBlob(r, fallback) {
    const name = (r.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || fallback;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** «الملفات اليومية»: the workbook the server saves every morning (month to date vs last
 *  month, every branch). Hidden when the reader may not have it (403) or it fails to load. */
function DailyFiles() {
    const { t, lang } = useI18n();
    const [files, setFiles] = useState(null);
    const [all, setAll] = useState(false);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/pos-insights/cashier-check/daily-files", { timeout: 30000 })
            .then((r) => { if (alive) setFiles(r.data?.files || []); })
            .catch(() => { if (alive) setFiles(null); });
        return () => { alive = false; };
    }, []);

    if (!files) return null;
    const download = async (f) => {
        setBusy(f.day);
        try {
            const r = await api.get(`/pos-insights/cashier-check/daily-files/${f.day}`, {
                responseType: "blob", timeout: EXPORT_TIMEOUT_MS,
            });
            saveBlob(r, `cashier_check_${f.day}.xlsx`);
        } catch (e) {
            toast.error((await blobErrorMessage(e, lang)) || t("pi_cc_daily_failed"));
        } finally {
            setBusy(null);
        }
    };
    const shown = all ? files : files.slice(0, DAILY_SHOWN);
    return (
        <div data-testid={PI.ccDaily} className="mb-3 rounded-xl border border-[color:var(--border-soft)] p-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="text-xs font-bold text-[color:var(--ink)]">{t("pi_cc_daily_title")}</p>
                <p className="text-[11px] text-[color:var(--ink-faint)]">{t("pi_cc_daily_hint")}</p>
            </div>
            {!files.length ? (
                <p className="text-[11px] text-[color:var(--ink-faint)]">{t("pi_cc_daily_none")}</p>
            ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                    {shown.map((f) => (
                        <Button key={f.day} type="button" variant="outline" data-testid={PI.ccDailyFile(f.day)}
                            onClick={() => download(f)} disabled={busy === f.day}
                            className="h-8 gap-1.5 px-2.5 text-xs font-semibold">
                            {busy === f.day ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Download className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />}
                            <bdi>{fmtDay(f.day, lang)}</bdi>
                            {!f.complete ? (
                                <span className="text-[10px] font-normal text-[color:var(--warning-soft-fg)]">
                                    ({t("pi_cc_daily_partial")})
                                </span>
                            ) : null}
                        </Button>
                    ))}
                    {files.length > DAILY_SHOWN ? (
                        <button type="button" data-testid={PI.ccDailyMore} onClick={() => setAll((v) => !v)}
                            className="px-1 text-xs font-semibold text-[color:var(--brand-brown)] underline-offset-2 hover:underline">
                            {all ? t("pi_cc_daily_less") : t("pi_cc_daily_more")}
                        </button>
                    ) : null}
                </div>
            )}
        </div>
    );
}

/** "YYYY-MM" -> the month's days, ending yesterday when it is the running month. */
function monthRange(month) {
    const start = `${month}-01`;
    const last = lastDayOfMonth(month);
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const yesterday = isoFromDate(y);
    return { start, end: last < yesterday ? last : (yesterday < start ? start : yesterday) };
}

function Change({ diff, pct }) {
    const { lang } = useI18n();
    if (diff === null || diff === undefined) return <span className="text-[color:var(--ink-faint)]">{"—"}</span>;
    const up = diff >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    const sign = up ? "+" : "−";
    return (
        <span className="inline-flex items-center justify-end gap-1 font-semibold"
            style={{ color: up ? "var(--success)" : "var(--danger)" }}>
            <Icon className="h-3.5 w-3.5" />
            <bdi dir="ltr" className="tnum">
                {sign}{fmtNumber(Math.abs(diff), lang, 2)}
                {pct === null || pct === undefined ? "" : ` (${sign}${fmtNumber(Math.abs(pct), lang, 1)}%)`}
            </bdi>
        </span>
    );
}

function Num({ v, int }) {
    const { lang } = useI18n();
    if (v === null || v === undefined) return <span className="text-[color:var(--ink-faint)]">{"—"}</span>;
    return <bdi dir="ltr" className="tnum">{int ? fmtInt(v, lang) : fmtNumber(v, lang, 2)}</bdi>;
}

function BranchBlock({ b, baseLabel, periodLabel, minOrders }) {
    const { t, lang } = useI18n();
    const name = lang === "ar" ? b.name_ar || b.name_en : b.name_en || b.name_ar;
    const cov = (k) => b.coverage?.[k] || {};
    const per = (fn) => ["base", "period"].map((k) => [k, fn(cov(k))]).filter(([, n]) => n > 0);
    const notes = [
        ["pi_cc_missing_days", per((c) => (c.days_in_range || 0) - (c.split_days || 0))],
        ["pi_cc_partial_days", per((c) => c.partial_days || 0)],
        ["pi_cc_open_reversals", per((c) => c.open_reversals || 0)],
    ].filter(([, list]) => list.length);
    const head = "text-[color:var(--ink-muted)] text-end whitespace-nowrap";

    return (
        <section data-testid={PI.ccBranch(b.pos_branch_id)}
            className="rounded-xl border border-[color:var(--border-soft)] p-3 sm:p-4">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-bold text-[color:var(--ink)]">{name}</h4>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[color:var(--ink-muted)]">
                    <span>{t("pi_cc_branch_avg")}:</span>
                    <span><Num v={b.base?.counter_avg} /> {lang === "ar" ? "←" : "→"} <Num v={b.period?.counter_avg} /></span>
                    <Change diff={b.counter_avg_change} pct={b.counter_avg_change_pct} />
                </div>
            </div>
            {notes.length ? (
                <div data-testid={PI.ccMissing(b.pos_branch_id)}
                    className="mb-2 space-y-0.5 text-[11px] text-[color:var(--warning-soft-fg)]">
                    {notes.map(([key, list]) => (
                        <p key={key}>
                            {t(key)}:{" "}
                            {list.map(([k, n]) => `${k === "base" ? baseLabel : periodLabel}: ${fmtInt(n, lang)}`)
                                .join(lang === "ar" ? "، " : ", ")}
                        </p>
                    ))}
                </div>
            ) : null}
            {!b.cashiers?.length ? (
                // with notes above, the days simply carry no order detail; without, there were no orders
                notes.length ? null : <p className="text-xs text-[color:var(--ink-faint)]">{t("pi_cc_no_data")}</p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                <TableHead className="text-start text-[color:var(--ink-muted)]">{t("pi_cc_cashier")}</TableHead>
                                <TableHead className={head}>{t("pi_cc_counter_orders")}<br /><span className="text-[10px] font-normal">{baseLabel}</span></TableHead>
                                <TableHead className={head}>{t("pi_cc_counter_avg")}<br /><span className="text-[10px] font-normal">{baseLabel}</span></TableHead>
                                <TableHead className={head}>{t("pi_cc_counter_orders")}<br /><span className="text-[10px] font-normal">{periodLabel}</span></TableHead>
                                <TableHead className={head}>{t("pi_cc_counter_avg")}<br /><span className="text-[10px] font-normal">{periodLabel}</span></TableHead>
                                <TableHead className={head}>{t("pi_cc_change")}</TableHead>
                                <TableHead className={head} title={t("pi_cc_voided_hint")}>{t("pi_cc_voided")}<br /><span className="text-[10px] font-normal">{periodLabel}</span></TableHead>
                                <TableHead className={head}>{t("pi_cc_share")}</TableHead>
                                <TableHead className={head}>{t("pi_cc_all_avg")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {b.cashiers.map((c, i) => (
                                <TableRow key={`${c.name}-${i}`} data-testid={PI.ccRow(b.pos_branch_id, i)}
                                    data-small={c.small_sample ? "true" : "false"}
                                    className={c.small_sample ? "opacity-60" : ""}>
                                    <TableCell className="text-start font-semibold text-[color:var(--ink)]">
                                        <bdi dir="auto">{c.name || "—"}</bdi>
                                        {c.small_sample ? (
                                            <span title={t("pi_cc_small_hint").replace("{n}", fmtInt(minOrders, lang))}
                                                className="ms-2 rounded-full bg-[color:var(--bg-surface-2)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--ink-muted)]">
                                                {t("pi_cc_small")}
                                            </span>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="text-end"><Num v={c.base?.counter_orders} int /></TableCell>
                                    <TableCell className="text-end"><Num v={c.base?.counter_avg} /></TableCell>
                                    <TableCell className="text-end"><Num v={c.period?.counter_orders} int /></TableCell>
                                    <TableCell className="text-end font-semibold"><Num v={c.period?.counter_avg} /></TableCell>
                                    <TableCell className="text-end"><Change diff={c.counter_avg_change} pct={c.counter_avg_change_pct} /></TableCell>
                                    <TableCell className="text-end text-[color:var(--ink-muted)]">
                                        {c.period?.voided_orders ? <Num v={c.period.voided_orders} int /> : "—"}
                                    </TableCell>
                                    <TableCell className="text-end text-[color:var(--ink-muted)]">
                                        {c.period?.counter_share_pct === null || c.period?.counter_share_pct === undefined
                                            ? "—"
                                            : <bdi dir="ltr" className="tnum">{fmtNumber(c.period.counter_share_pct, lang, 0)}%</bdi>}
                                    </TableCell>
                                    <TableCell className="text-end text-[color:var(--ink-muted)]"><Num v={c.period?.avg} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </section>
    );
}

/**
 * «متوسط فاتورة الكاشير» — per branch and POS login, the average check of the
 * counter orders (paid takeaway / dine-in) in a month against a base month.
 * App, own-delivery / call-centre and damaged orders are left out of it, and so
 * is a sale the POS reversed later (an RTRN row); the login's share of counter
 * orders, its reversed sales and its all-orders average sit beside it.
 * `withBranchFilter` (the standalone «متوسط فاتورة الكاشير» screen): the panel keeps its own
 * branch filter, listing the branches of its first all-branch reply; otherwise the page's
 * filter comes in as `branchId`.
 */
export default function PosCashierCheckPanel({ branchId: pageBranchId, withBranchFilter = false }) {
    const { t, lang } = useI18n();
    const [ownBranch, setOwnBranch] = useState("all");
    const [branchOptions, setBranchOptions] = useState([]);
    const branchId = withBranchFilter ? ownBranch : pageBranchId;
    const months = useMemo(() => monthOptions(), []);
    // opens on this month to date (refreshed every morning) against last month
    const first = Math.max(0, months.indexOf(yesterdayMonth()));
    const [period, setPeriod] = useState(months[first]);
    const [base, setBase] = useState(months[first + 1]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);

    const params = useMemo(() => {
        const p = monthRange(period);
        const b = monthRange(base);
        return {
            start: p.start, end: p.end, base_start: b.start, base_end: b.end,
            ...(branchId && branchId !== "all" ? { branch_ids: branchId } : {}),
        };
    }, [period, base, branchId]);

    // a reply for an earlier selection never replaces the current one
    useEffect(() => {
        let alive = true;
        setLoading(true);
        setFailed(false);
        api.get("/pos-insights/cashier-check", { params, timeout: 60000 })
            .then((r) => {
                if (!alive) return;
                setData(r.data);
                if (!params.branch_ids && r.data?.branches?.length) {
                    setBranchOptions(r.data.branches.map((b) => ({ id: b.branch_id, name_ar: b.name_ar, name_en: b.name_en })));
                }
            })
            .catch(() => { if (alive) { setData(null); setFailed(true); } })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [params]);

    const exportXlsx = async () => {
        setBusy(true);
        try {
            const r = await api.get("/pos-insights/cashier-check/export", {
                params: { ...params, lang }, responseType: "blob", timeout: EXPORT_TIMEOUT_MS,
            });
            saveBlob(r, `cashier_check_${period}.xlsx`);
            toast.success(t("pi_cc_exported"));
        } catch (e) {
            toast.error((await blobErrorMessage(e, lang)) || t("pi_cc_export_failed"));
        } finally {
            setBusy(false);
        }
    };

    const monthSelect = (value, onChange, testId, label) => (
        <div className="flex items-center gap-1.5">
            <span className="text-xs text-[color:var(--ink-muted)]">{label}</span>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testId} className="h-9 w-[150px] text-sm">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {months.map((m) => <SelectItem key={m} value={m}>{fmtPeriod(m, lang)}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );

    const periodLabel = fmtPeriod(period, lang);
    const baseLabel = fmtPeriod(base, lang);

    return (
        <PosCard icon={Receipt} title={t("pi_cc_title")} desc={t("pi_cc_desc")} testId={PI.cashierCheck}
            right={
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {withBranchFilter ? (
                        <Select value={ownBranch} onValueChange={setOwnBranch}>
                            <SelectTrigger data-testid={PI.ccBranchFilter} className="h-9 w-[170px] text-sm">
                                <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                <SelectValue placeholder={t("pi_branch_all")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("pi_branch_all")}</SelectItem>
                                {branchOptions.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {lang === "ar" ? b.name_ar || b.name_en : b.name_en || b.name_ar}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : null}
                    {monthSelect(period, setPeriod, PI.ccPeriod, t("pi_cc_period"))}
                    {monthSelect(base, setBase, PI.ccBase, t("pi_cc_base"))}
                    <Button type="button" variant="outline" data-testid={PI.ccExport} onClick={exportXlsx}
                        disabled={busy || loading || !data?.available} className="h-9 gap-1.5 text-xs font-semibold">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <FileSpreadsheet className="h-4 w-4 text-[color:var(--brand-brown)]" />}
                        {t("pi_cc_export")}
                    </Button>
                </div>
            }>
            <DailyFiles />
            {loading ? (
                <div className="space-y-2">
                    <Skeleton className="h-8 w-full rounded-lg" />
                    <Skeleton className="h-40 w-full rounded-xl" />
                </div>
            ) : failed ? (
                <EmptyState compact testId={PI.ccEmpty} title={t("pi_cc_load_failed")} />
            ) : !data?.available || !data.branches?.length ? (
                <EmptyState compact testId={PI.ccEmpty}
                    title={data?.reason === "no_permitted_branches" ? t("pi_no_branches") : t("no_data")} />
            ) : (
                <div className="space-y-3">
                    <p className="text-[11px] text-[color:var(--ink-faint)]">
                        {t("pi_cc_small_hint").replace("{n}", fmtInt(data.min_counter_orders, lang))}
                    </p>
                    <p data-testid={PI.ccBackfill} className="text-[11px] text-[color:var(--ink-faint)]">
                        {t("pi_cc_backfill")}:{" "}
                        <bdi>{data.backfill?.finished_at && !data.backfill.error ? fmtRiyadhDateTime(data.backfill.finished_at, lang) : t("pi_cc_backfill_none")}</bdi>
                    </p>
                    {data.branches.map((b) => (
                        <BranchBlock key={b.branch_id} b={b} baseLabel={baseLabel} periodLabel={periodLabel}
                            minOrders={data.min_counter_orders} />
                    ))}
                </div>
            )}
        </PosCard>
    );
}
